import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import style from "./Monetization.module.css";
import { showToast } from "../../utils/Toastify/Toastify";
import {
  getMonetizationCreators,
  updateCreatorRevenueConfig,
  creatorsDelete,
  creatorStatusChange,
} from "../../api/HttpApis";
import CreatorInvite from "../CreatorInvite/CreatorInvite";
import ListView from "../../component/ListView/ListView";
import { useRbac } from "../../rbac/RbacContext";
import useUrlTab from "../../hooks/useUrlTab";
import ShareInviteModal from "./ShareInviteModal";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en", { day: "numeric", month: "short", year: "numeric" }) : "—";

const MODELS = [
  { value: "1_month", label: "1 Month" },
  { value: "2_month", label: "2 Months" },
  { value: "lifetime", label: "Lifetime" },
];

// Tab key -> the `status` query param the backend understands (see
// AdminMonetizationController.creators).
const CREATOR_TABS = [
  { key: "all", label: "All" },
  { key: "approved", label: "Approved" },
  { key: "declined", label: "Declined" },
  { key: "under_review", label: "Under Review" },
  { key: "changes_requested", label: "Changes Requested" },
];
const CREATOR_TAB_KEYS = CREATOR_TABS.map((t) => t.key);

function ConfigModal({ creator, onClose, onSaved }) {
  const [model, setModel] = useState(creator.config.affiliate_model || "lifetime");
  const [percent, setPercent] = useState(String(creator.config.affiliate_percent ?? 50));
  const [watchEligible, setWatchEligible] = useState(creator.config.watch_pool_eligible ?? true);
  const [enabled, setEnabled] = useState(creator.config.is_enabled ?? true);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const pct = Number(percent);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      showToast("Percentage must be between 0 and 100", "error");
      return;
    }
    setSaving(true);
    try {
      await updateCreatorRevenueConfig(creator.id, {
        affiliate_model: model,
        affiliate_percent: pct,
        watch_pool_eligible: watchEligible,
        is_enabled: enabled,
      });
      showToast("Creator revenue config updated", "success");
      onSaved();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to update config", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={style.modalOverlay} onClick={onClose}>
      <div className={style.modal} onClick={(e) => e.stopPropagation()}>
        <h3 className={style.modalTitle}>Revenue Settings — {creator.name}</h3>

        <div className={style.formGroup} style={{ marginBottom: 16 }}>
          <label>Affiliate Model</label>
          <select className={style.select} value={model} onChange={(e) => setModel(e.target.value)}>
            {MODELS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <span className={style.helper}>
            How long this creator earns from a referred subscriber.
          </span>
        </div>

        <div className={style.formGroup} style={{ marginBottom: 16 }}>
          <label>Affiliate Percentage (%)</label>
          <input
            className={style.input}
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
          />
          <span className={style.helper}>Share of each referred subscription payment.</span>
        </div>

        <div className={style.formGroup} style={{ marginBottom: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={watchEligible}
              onChange={(e) => setWatchEligible(e.target.checked)}
            />
            Eligible for watch-time pool
          </label>
        </div>

        <div className={style.formGroup}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Monetization enabled
          </label>
        </div>

        <div className={style.formActions}>
          <button className={style.btn} onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
          <button className={`${style.btn} ${style.btnGhost}`} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CreatorRevenue() {
  const navigate = useNavigate();
  const { search: listQuery } = useLocation();
  // Hand the detail page this list's ?tab=/&view= so its Back / Save land on
  // the same tab instead of "All".
  const openCreator = (id) =>
    navigate(`/mainlayout/monetization/creators/${id}`, {
      state: { from: listQuery },
    });
  const [creators, setCreators] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [statusTab, setStatusTab] = useUrlTab(CREATOR_TAB_KEYS, "all");
  const [statusChangingId, setStatusChangingId] = useState(null);
  const { can } = useRbac();
  const limit = 20;

  useEffect(() => {
    const h = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(h);
  }, [searchInput]);

  // Id of the newest list request. Quick tab/search/page changes overlap, so a
  // reply that isn't the latest is dropped — otherwise a slow "Under Review"
  // reply could land under "Changes Requested".
  const latestRequest = useRef(0);
  // The page ListView is showing, so a refresh after an edit reloads it rather
  // than page 1 under a pager still highlighting the old page.
  const shownPage = useRef({ page: 1, perPage: limit });

  // ListView drives every list fetch via onPageChange(page, perPage): the first
  // load, page changes, and — since it gets `activeTab` and `queryKey={search}`
  // — page 1 after a tab or search change. No fetch effect here, so a tab
  // switch is one request, not two.
  const fetchCreators = useCallback(
    async (page = 1, perPage = limit) => {
      shownPage.current = { page, perPage };
      const requestId = ++latestRequest.current;
      setLoading(true);
      try {
        const res = await getMonetizationCreators({ page, limit: perPage, search, status: statusTab });
        if (requestId !== latestRequest.current) return;
        // Backend envelope: { status, data: { data: [...], total } }
        const payload = res?.data ?? res ?? {};
        setCreators(Array.isArray(payload.data) ? payload.data : []);
        setTotal(payload.total || 0);
      } catch (err) {
        if (requestId !== latestRequest.current) return;
        showToast(err?.response?.data?.message || "Failed to load creators", "error");
        setCreators([]);
      } finally {
        if (requestId === latestRequest.current) setLoading(false);
      }
    },
    [search, statusTab]
  );
  // Reload the current page after a delete / status change / config save.
  // Goes through a ref to the latest fetchCreators: these run after an await,
  // and the copy captured at click time would refetch the tab/search that was
  // showing then — landing the old tab's rows under a tab switched to meanwhile.
  const fetchCreatorsRef = useRef(fetchCreators);
  fetchCreatorsRef.current = fetchCreators;
  const refreshCreators = () =>
    fetchCreatorsRef.current(shownPage.current.page, shownPage.current.perPage);

  // Delete a creator (same API the People-menu list used). Confirms first.
  // The backend now refuses this (422, with a specific reason) instead of
  // silently cascading if the creator has real earnings/payout/watch-time
  // history — that error message surfaces via the toast below, and
  // Deactivate (added alongside this) is the real alternative it points to.
  const handleDelete = async (creator) => {
    if (!window.confirm(`Delete creator "${creator.name}"? This cannot be undone.`)) return;
    try {
      const res = await creatorsDelete(creator.id);
      showToast(res?.data?.message || "Creator deleted", "success");
      refreshCreators();
    } catch (err) {
      showToast(err?.response?.data?.message || "Error deleting creator", "error");
    }
  };

  const handleStatusChange = async (creator, nextStatus) => {
    const verb = nextStatus === "Inactive" ? "deactivate" : "reactivate";
    if (!window.confirm(`Are you sure you want to ${verb} "${creator.name}"?`)) return;
    setStatusChangingId(creator.id);
    try {
      const res = await creatorStatusChange(creator.id, nextStatus);
      showToast(res?.data?.message || `Creator ${verb}d`, "success");
    } catch (err) {
      showToast(err?.response?.data?.message || `Error trying to ${verb} creator`, "error");
    } finally {
      setStatusChangingId(null);
      refreshCreators();
    }
  };

  const cardFor = (c) => ({
    id: c.id,
    image: c.profile_img,
    thumbLabel: initials(c.name),
    title: c.name,
    meta: [
      c.email,
      `${c.total_content || 0} contributions`,
      `WT (lifetime) ${money(c.watch_time_revenue_cents)}`,
      `Sub (lifetime) ${money(c.subscription_revenue_cents)}`,
    ]
      .filter(Boolean)
      .join(" · "),
    badge: c.config
      ? {
          label: c.config.is_enabled ? "Enabled" : "Disabled",
          tone: c.config.is_enabled ? "success" : "neutral",
        }
      : null,
    onClick: () => openCreator(c.id),
    actions: [
      {
        icon: "view",
        label: "View details",
        onClick: () => openCreator(c.id),
      },
      { icon: "edit", label: "Configure revenue", onClick: () => setEditing(c) },
      ...(can("users", "create") && c.status === "Invited" && c.email
        ? [{ icon: "link", label: "Share invite link", onClick: () => setSharing(c) }]
        : []),
      ...(can("users", "edit") && (c.status === "Approved" || c.status === "Inactive")
        ? [
            c.status === "Inactive"
              ? {
                  icon: "accept",
                  label: "Activate",
                  tone: "success",
                  disabled: statusChangingId === c.id,
                  onClick: () => handleStatusChange(c, "Approved"),
                }
              : {
                  icon: "reject",
                  label: "Deactivate",
                  tone: "danger",
                  disabled: statusChangingId === c.id,
                  onClick: () => handleStatusChange(c, "Inactive"),
                },
          ]
        : []),
      { icon: "delete", label: "Delete creator", onClick: () => handleDelete(c) },
    ],
  });

  const sumCents = (key) =>
    creators.reduce((acc, c) => acc + (c[key] || 0), 0);
  const stats = [
    { label: "Total Creators", value: (total || 0).toLocaleString() },
    {
      label: "Watch Revenue",
      value: money(sumCents("watch_time_revenue_cents")),
      sub: "Lifetime, this page only",
    },
    {
      label: "Subscription Revenue",
      value: money(sumCents("subscription_revenue_cents")),
      sub: "Lifetime, this page only",
    },
  ];

  return (
    <div className={style.page}>
      <div className={style.toolbar} style={{ marginBottom: 8 }}>
        <input
          className={style.search}
          placeholder="Search creators..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      <ListView
        heading="Creator List"
        rowsOnly
        data={creators}
        getCard={cardFor}
        stats={stats}
        tabs={CREATOR_TABS}
        activeTab={statusTab}
        onTabChange={setStatusTab}
        queryKey={search}
        totalCount={total}
        onPageChange={fetchCreators}
        tableLoading={loading}
        addLabel="New Creator"
        onAdd={() => setInviteOpen(true)}
      />

      {editing && (
        <ConfigModal
          creator={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refreshCreators();
          }}
        />
      )}

      {sharing && <ShareInviteModal creator={sharing} onClose={() => setSharing(null)} />}

      {/* Reuses the existing invite modal (same form + API as the People list). */}
      <CreatorInvite
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInviteSuccess={() => {
          setInviteOpen(false);
          refreshCreators();
        }}
      />
    </div>
  );
}
